import {
  Account,
  AccountPosting,
  AccountType,
  AccountTypeLabels,
  FloatLoadType,
  FloatLoadTypeLabels,
  PostingType,
} from '../types/account';

/**
 * Format currency amount with proper symbol and decimals
 */
export const formatCurrency = (amount: number, currency: string): string => {
  const currencySymbols: Record<string, string> = {
    USD: '$',
    ZWG: 'ZWG',
  };

  const symbol = currencySymbols[currency] || currency;
  const formattedAmount = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Math.abs(amount));

  if (amount < 0) {
    return `${symbol === '$' ? '-$' : '-'}${formattedAmount}${symbol === 'ZWG' ? ' ZWG' : ''}`;
  }

  return `${symbol === '$' ? '$' : ''}${formattedAmount}${symbol === 'ZWG' ? ' ZWG' : ''}`;
};

/**
 * Format balance with color based on positive/negative
 */
export const getBalanceColor = (amount: number): string => {
  if (amount > 0) return 'success.main';
  if (amount < 0) return 'error.main';
  return 'text.primary';
};

/**
 * Format account number for display (add spacing for readability)
 */
export const formatAccountNumber = (accountNumber: string): string => {
  // Format like: ACCT-MER-123-USD → ACCT-MER-123-USD (no change needed, already formatted)
  return accountNumber;
};

/**
 * Get human-readable account type label
 */
export const getAccountTypeLabel = (accountType: AccountType | string): string => {
  return AccountTypeLabels[accountType as AccountType] || accountType;
};

/**
 * Get human-readable float load type label
 */
export const getFloatLoadTypeLabel = (loadType: FloatLoadType | string): string => {
  return FloatLoadTypeLabels[loadType as FloatLoadType] || loadType;
};

/**
 * Validate double-entry bookkeeping (debits = credits)
 */
export const validateDoubleEntry = (postings: AccountPosting[]): {
  isBalanced: boolean;
  totalDebits: number;
  totalCredits: number;
  difference: number;
} => {
  const totalDebits = postings
    .filter((p) => p.postingType === PostingType.DEBIT)
    .reduce((sum, p) => sum + p.amount, 0);

  const totalCredits = postings
    .filter((p) => p.postingType === PostingType.CREDIT)
    .reduce((sum, p) => sum + p.amount, 0);

  const difference = Math.abs(totalDebits - totalCredits);
  const isBalanced = difference < 0.01; // Allow for rounding errors

  return {
    isBalanced,
    totalDebits,
    totalCredits,
    difference,
  };
};

/**
 * Calculate running balance from postings
 */
export const calculateRunningBalance = (
  postings: AccountPosting[],
  startingBalance: number = 0
): AccountPosting[] => {
  let runningBalance = startingBalance;

  return postings.map((posting) => {
    if (posting.postingType === PostingType.DEBIT) {
      runningBalance += posting.amount;
    } else {
      runningBalance -= posting.amount;
    }

    return {
      ...posting,
      balanceAfter: runningBalance,
    };
  });
};

/**
 * Get account type badge color
 */
export const getAccountTypeBadgeColor = (
  accountType: AccountType | string
): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
  const type = accountType as AccountType;

  switch (type) {
    case AccountType.MERCHANT_ACCOUNT:
      return 'primary';
    case AccountType.RETAILER_ACCOUNT:
      return 'secondary';
    case AccountType.AGENT_ACCOUNT:
      return 'info';
    case AccountType.VOUCHER_CLEARING:
    case AccountType.COMMISSION_REVENUE:
    case AccountType.MERCHANT_PAYABLE:
    case AccountType.RETAILER_RECEIVABLE:
      return 'warning';
    case AccountType.PSP_BANK_ACCOUNT:
    case AccountType.SETTLEMENT_ACCOUNT:
      return 'success';
    case AccountType.AGENT_POOL_USD:
    case AccountType.AGENT_POOL_ZWG:
    case AccountType.AGENT_POOL_ZAR:
      return 'warning';
    default:
      return 'default';
  }
};

/**
 * Get posting type color
 */
export const getPostingTypeColor = (postingType: PostingType | string): string => {
  return postingType === PostingType.DEBIT ? 'error.main' : 'success.main';
};

/**
 * Get currency symbol
 */
export const getCurrencySymbol = (currency: string): string => {
  const symbols: Record<string, string> = {
    USD: '$',
    ZWG: 'ZWG',
  };
  return symbols[currency] || currency;
};

/**
 * Check if account has low balance (below threshold)
 */
export const hasLowBalance = (account: Account, threshold: number = 100): boolean => {
  return account.availableBalance < threshold && account.availableBalance >= 0;
};

/**
 * Check if account has negative balance
 */
export const hasNegativeBalance = (account: Account): boolean => {
  return account.currentBalance < 0;
};

/**
 * Get account status badge props
 */
export const getAccountStatusBadge = (account: Account): {
  label: string;
  color: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
} => {
  if (!account.isActive) {
    return { label: 'Inactive', color: 'default' };
  }

  if (hasNegativeBalance(account)) {
    return { label: 'Negative Balance', color: 'error' };
  }

  if (hasLowBalance(account)) {
    return { label: 'Low Balance', color: 'warning' };
  }

  return { label: 'Active', color: 'success' };
};

/**
 * Format date for display
 */
export const formatDate = (dateString: string | undefined | null): string => {
  if (!dateString) return 'N/A';

  const date = new Date(dateString);
  if (isNaN(date.getTime())) {
    return 'Invalid Date';
  }

  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(date);
};

/**
 * Format datetime for display
 */
export const formatDateTime = (dateString: string | undefined | null): string => {
  if (!dateString) return 'N/A';

  const date = new Date(dateString);
  if (isNaN(date.getTime())) {
    return 'Invalid Date';
  }

  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

/**
 * Generate account reference display text
 */
export const getAccountReferenceDisplay = (account: Account): string => {
  if (account.partnerName) {
    return `${account.accountName} (${account.partnerName})`;
  }
  return account.accountName;
};

/**
 * Check if account is a control account
 */
export const isControlAccount = (accountType: AccountType | string): boolean => {
  const controlAccountTypes = [
    AccountType.VOUCHER_CLEARING,
    AccountType.COMMISSION_REVENUE,
    AccountType.MERCHANT_PAYABLE,
    AccountType.RETAILER_RECEIVABLE,
    AccountType.AGENT_POOL_USD,
    AccountType.AGENT_POOL_ZWG,
    AccountType.AGENT_POOL_ZAR,
  ];
  return controlAccountTypes.includes(accountType as AccountType);
};

/**
 * Check if account is a partner account
 */
export const isPartnerAccount = (accountType: AccountType | string): boolean => {
  const partnerAccountTypes = [
    AccountType.MERCHANT_ACCOUNT,
    AccountType.RETAILER_ACCOUNT,
    AccountType.AGENT_ACCOUNT,
  ];
  return partnerAccountTypes.includes(accountType as AccountType);
};

/**
 * Get posting type icon
 */
export const getPostingTypeIcon = (postingType: PostingType | string): string => {
  return postingType === PostingType.DEBIT ? '−' : '+';
};

/**
 * Calculate percentage change
 */
export const calculatePercentageChange = (current: number, previous: number): number => {
  if (previous === 0) return current > 0 ? 100 : 0;
  return ((current - previous) / Math.abs(previous)) * 100;
};

/**
 * Format percentage
 */
export const formatPercentage = (value: number): string => {
  return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
};

/**
 * Truncate text with ellipsis
 */
export const truncateText = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) return text;
  return `${text.substring(0, maxLength)}...`;
};

/**
 * Export data to CSV
 */
export const exportToCSV = (data: any[], filename: string): void => {
  if (data.length === 0) return;

  const headers = Object.keys(data[0]);
  const csvContent = [
    headers.join(','),
    ...data.map((row) =>
      headers.map((header) => {
        const value = row[header];
        // Escape quotes and wrap in quotes if contains comma
        if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
          return `"${value.replace(/"/g, '""')}"`;
        }
        return value;
      }).join(',')
    ),
  ].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};
